import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Doctor } from '../models/doctor.model';
import { Notifications } from '../models/notifications.model';
import { LoginResponseModel } from '../models/login-response.model';
import { BYPASS_INTERCEPTOR } from './login.service';
import { DoctorSubscriptionsModel } from '../models/doctor-subscriptions.model';
import { User } from '../models/user.model';
import { environment } from '../../environments/environment';
import { PendingClinicInvitationResponse } from '../models/pending-clinic-invitation-response.model';
import { RespondToInvitationRequest } from '../models/respond-to-invitation-request.model';
import { ClinicProfileResponse } from '../models/clinic-profile-response.model';
import { Router } from '@angular/router';
import { PermissionService } from './permission.service';
import { ClinicService } from './clinic.service';
import { ClinicStatusService } from './clinic-status.service';
import { UserRole, UserRolePermissions } from '../enums/user-role.enum';
import { DoctorProfileStatus } from '../enums/doctor-profile-status.enum';
import { Clinic } from '../models/clinic.model';

@Injectable({
  providedIn: 'root',
})
export class DoctorProfileService {
  private apiUrl = environment.APP_BASE_URL + '/doctor-portal/doctor-profile'; // replace with real API endpoint
  private userProfileUrl = environment.APP_BASE_URL + '/clinic-portal/user-profile';

  constructor(
    private http: HttpClient,
    private permissionService: PermissionService,
    private router: Router,
    private clinicService: ClinicService,
    private clinicStatusService: ClinicStatusService,
  ) {}

  public getUserProfile(): Observable<User> {
    return this.http.get<User>(this.userProfileUrl);
  }

  public getDoctorProfileDetails(): Observable<Doctor> {
    return this.http.get<Doctor>(`${this.apiUrl}`, {});
  }

  public getDoctorSubscriptions(): Observable<DoctorSubscriptionsModel> {
    return this.http.get<DoctorSubscriptionsModel>(`${this.apiUrl}/subscriptions`, {});
  }

  public createDoctorProfile(doctor: Doctor): Observable<Doctor> {
    return this.http.put<Doctor>(`${this.apiUrl}`, doctor);
  }

  public updateDoctorNotifications(notifications: Notifications): Observable<Notifications> {
    return this.http.patch<Notifications>(`${this.apiUrl}/notifications`, notifications);
  }

  public updateDoctorOnlineStatus(status: boolean): Observable<boolean> {
    return this.http.put<boolean>(`${this.apiUrl}/online/status/${status}`, {});
  }

  public getDoctorOnlineStatus(): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/online/status`, {});
  }

  public getDoctorBannerStatus(): Observable<string> {
    return this.http.get(`${this.apiUrl}/banner/status`, {
      responseType: 'text',
    });
  }

  public saveClinicHours(doctor: Doctor | undefined): Observable<Doctor> {
    return this.http.patch<Doctor>(`${this.apiUrl}/hours`, doctor);
  }

  public saveBio(doctor: Doctor | undefined): Observable<Doctor> {
    return this.http.patch<Doctor>(`${this.apiUrl}/bio`, doctor);
  }

  public savePreBookingNotes(doctor: Doctor | undefined): Observable<Doctor> {
    return this.http.patch<Doctor>(`${this.apiUrl}/bio`, doctor);
  }

  public requestOtp(email: string | undefined, type: string | undefined): Observable<string> {
    return this.http.get(`${this.apiUrl}/request-otp/${email}/${type}`, { responseType: 'text' });
  }

  public requestOtpUsingSession(): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/request-otp/RESET_PASSWORD`);
  }

  public verifyOtp(
    userId: string | undefined,
    type: string | undefined,
    otp: string | undefined,
  ): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/verify-otp/${userId}/${otp}/${type}`, {});
  }

  public verifyOtpUsingSession(
    type: string | undefined,
    otp: string | undefined,
  ): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/verify-otp/${otp}/${type}`, {});
  }

  public changePassword(
    doctorId: string | undefined,
    type: string | undefined,
    password: string | undefined,
  ): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/change-password/${doctorId}/${type}`, {
      password: password,
    });
  }

  /**
   * Step 2 of the forgot-password pre-auth flow.
   * Sends the OTP and the pre-auth session token (as X-Pre-Auth-Token header) to verify.
   */
  public verifyOtpWithPreAuth(
    type: string | undefined,
    otp: string | undefined,
    preAuthToken: string | undefined,
  ): Observable<boolean> {
    const headers: any = {};
    if (preAuthToken && preAuthToken.trim() !== '') {
      headers['X-Pre-Auth-Token'] = preAuthToken;
    }

    return this.http.post<boolean>(`${this.apiUrl}/verify-otp/${otp}/${type}`, {}, { headers });
  }

  /**
   * Step 3 of the forgot-password pre-auth flow.
   * Changes the password using the OTP-verified pre-auth session token.
   */
  public changePasswordWithPreAuth(
    type: string | undefined,
    password: string | undefined,
    preAuthToken: string | undefined,
  ): Observable<boolean> {
    const headers: any = {};
    if (preAuthToken && preAuthToken.trim() !== '') {
      headers['X-Pre-Auth-Token'] = preAuthToken;
    }

    return this.http.post<boolean>(
      `${this.apiUrl}/change-password/pre-auth/${type}`,
      { password: password },
      { headers },
    );
  }

  public changePasswordUsingSession(
    type: string | undefined,
    password: string | undefined,
  ): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/change-password/${type}`, {
      password: password,
    });
  }

  public getPendingClinicInvitations(): Observable<PendingClinicInvitationResponse[]> {
    return this.http.get<PendingClinicInvitationResponse[]>(`${this.apiUrl}/pending-invitations`);
  }

  public getClinicProfile(): Observable<ClinicProfileResponse> {
    return this.http.get<ClinicProfileResponse>(`${this.apiUrl}/clinic-profile`);
  }

  public canCreateClinic(): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}/clinic-creation-eligibility`);
  }

  public createClinic(clinic: Clinic): Observable<Clinic> {
    return this.http.post<Clinic>(`${this.apiUrl}/clinic`, clinic);
  }

  public respondToInvitation(request: RespondToInvitationRequest): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/respond`, request);
  }

  public setActiveProfileRole(role: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.userProfileUrl}/set-active-role/${role}`, {});
  }

  public updateUserProfile(user: User): Observable<User> {
    return this.http.put<User>(this.userProfileUrl, user);
  }

  public setActiveClinic(clinicId: string): Observable<void> {
    return this.http.post<void>(`${this.userProfileUrl}/active-clinic/${clinicId}`, {});
  }

  public switchClinicContext(clinicId: string): void {
    this.setActiveClinic(clinicId).subscribe({
      next: () => this.completeClinicSwitch(clinicId),
      error: () => this.completeClinicSwitch(clinicId),
    });
  }

  private completeClinicSwitch(clinicId: string): void {
    localStorage.setItem('activeClinicId', clinicId);
    localStorage.setItem('clinicId', clinicId);
    sessionStorage.setItem('activeClinicId', clinicId);
    sessionStorage.setItem('clinicId', clinicId);

    this.clinicStatusService.refresh();
    this.getUserProfile().subscribe({
      next: (res) => {
        localStorage.setItem('userProfile', JSON.stringify(res));
        localStorage.setItem('availableUserRoles', JSON.stringify(res.roles));
        localStorage.removeItem('clinicDoctors');

        const activeRole = res.currentActiveRole || res.roles[0];

        if (activeRole === 'DOCTOR') {
          localStorage.setItem('userRole', 'DOCTOR');
          this.permissionService.setPermissions(UserRolePermissions[UserRole.DOCTOR]);
          this.getDoctorProfileDetails().subscribe({
            next: (docRes) => {
              if (docRes.profileStatus === DoctorProfileStatus.DOCTOR_NOT_ONBOARDED) {
                localStorage.setItem('doctorLoad', JSON.stringify(docRes));
                this.router.navigate(['dashboard/doctor-onboard']).then(() => {
                  window.location.reload();
                });
              } else {
                this.router
                  .navigate(['dashboard/doctor-dashboard'], {
                    state: { doctorData: docRes },
                  })
                  .then(() => {
                    window.location.reload();
                  });
              }
            },
            error: () => {
              window.location.reload();
            },
          });
        } else if (activeRole === 'MANAGER') {
          localStorage.setItem('userRole', 'MANAGER');
          this.permissionService.setPermissions(UserRolePermissions[UserRole.MANAGER]);
          this.router
            .navigate(['dashboard/manager-dashboard'], {
              state: { managerData: res },
            })
            .then(() => {
              window.location.reload();
            });
        } else if (activeRole === 'RECEPTION') {
          localStorage.setItem('userRole', 'RECEPTION');
          this.permissionService.setPermissions(UserRolePermissions[UserRole.RECEPTION]);
          this.clinicService.getClinicDoctorProfiles().subscribe({
            next: (doctors) => {
              localStorage.setItem('clinicDoctors', JSON.stringify(doctors));
              this.router
                .navigate(['dashboard/reception-dashboard'], {
                  state: { receptionData: res },
                })
                .then(() => {
                  window.location.reload();
                });
            },
            error: () => {
              this.router
                .navigate(['dashboard/reception-dashboard'], {
                  state: { receptionData: res },
                })
                .then(() => {
                  window.location.reload();
                });
            },
          });
        } else {
          window.location.reload();
        }
      },
      error: (err) => {
        console.error('Failed to switch clinic profile details', err);
        window.location.reload();
      },
    });
  }
}
